import { useCallback, useMemo, useState } from "react";
import { multiFieldSearch } from "@/core/utils/multi-field-search";
import { getMissingTechApprovalAccessKeys } from "@/payroll/approvals/utils";
import {
  useGetPayrollStatementPackApprovals,
  usePayrollStatementDeliveryPolling,
  usePayrollStatementNotifications,
  useRetryPayrollStatementProjectionBuild,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/query";
import {
  SIDEBAR_VIEW,
  type SidebarView,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/sidebar-view";
import type { PayrollStatementNotificationRecipient } from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import { useKnownPackApprovals } from "@/payroll/pay-breakdown/payroll-statement-notifications/use-known-pack-approvals";
import { usePayrollStatementNoteDrafts } from "@/payroll/pay-breakdown/payroll-statement-notifications/use-note-drafts";
import { useRecipientSelection } from "@/payroll/pay-breakdown/payroll-statement-notifications/use-recipient-selection";
import { useSendPayrollStatements } from "@/payroll/pay-breakdown/payroll-statement-notifications/use-send-payroll-statements";
import { useSentPackDriftRefresh } from "@/payroll/pay-breakdown/payroll-statement-notifications/use-sent-pack-drift-refresh";
import {
  formatNotificationPeriod,
  getBlockedChangedPackRecipients,
  getRecipientsWithChangedPacks,
  getSendDisabledReason,
  mergePolledPackApprovals,
  sortPayrollStatementRecipients,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/utils";
import {
  PAYROLL_EMAILS_ACCESS,
  type PayrollEmailsAccess,
} from "@/payroll/pay-breakdown/types";
import {
  useGetPayPeriodDetail,
  useGetPayPeriods,
} from "@/payroll/pay-periods/query";
import { useTeamFilter } from "@/payroll/teams/use-team-filter";

const EMPTY_RECIPIENTS: PayrollStatementNotificationRecipient[] = [];

export interface PacksSectionInput {
  businessId: string | null;
  periodStart: string | null;
  payrollEmailsAccess: PayrollEmailsAccess;
  awaitingReplyTechnicianIds?: Set<string>;
}

export const usePacksSectionState = ({
  businessId,
  periodStart,
  payrollEmailsAccess,
  awaitingReplyTechnicianIds,
}: PacksSectionInput) => {
  const { teams, teamIds, setTeamIds } = useTeamFilter(
    periodStart ? businessId : null,
  );
  const canUsePayrollEmails =
    payrollEmailsAccess === PAYROLL_EMAILS_ACCESS.enabled;
  const previewOnly = !canUsePayrollEmails;
  const [detailTechnicianId, setDetailTechnicianId] = useState<string | null>(
    null,
  );
  const [sidebarView, setSidebarView] = useState<SidebarView>(
    SIDEBAR_VIEW.preview,
  );
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [technicianSearch, setTechnicianSearch] = useState("");

  const {
    data,
    isLoading,
    isFetching: isFetchingNotifications,
    dataUpdatedAt: notificationsUpdatedAt,
    errorUpdatedAt: notificationsErroredAt,
    error,
    projectionBuild,
    canRetryProjectionHydration,
    isProjectionRefreshing,
    refetchProjectionBuild,
    refetch: refetchNotifications,
  } = usePayrollStatementNotifications(businessId, periodStart ?? null);
  const { mutate: retryProjectionBuild, isPending: isRetryingProjection } =
    useRetryPayrollStatementProjectionBuild(businessId, periodStart ?? null);
  const { data: payPeriods = [] } = useGetPayPeriods(businessId);
  const selectedPeriod = payPeriods.find(
    (period) => period.start_date === periodStart,
  );
  const periodId = selectedPeriod?.id;
  const { data: periodDetail } = useGetPayPeriodDetail(businessId, periodId, {
    runId: selectedPeriod?.latest_completed_ai_script_run_id,
  });
  const { data: freshApprovals, dataUpdatedAt: approvalsUpdatedAt } =
    useGetPayrollStatementPackApprovals(
      businessId,
      periodStart,
      !!data && !previewOnly,
    );
  const knownApprovals = useKnownPackApprovals({
    recipients: data?.recipients,
    polled: freshApprovals,
    payloadFetching: isFetchingNotifications,
    payloadUpdatedAt: notificationsUpdatedAt,
    payloadErroredAt: notificationsErroredAt,
  });
  const recipients = useMemo(
    () =>
      mergePolledPackApprovals(
        sortPayrollStatementRecipients(
          data?.recipients ?? EMPTY_RECIPIENTS,
          awaitingReplyTechnicianIds,
        ),
        freshApprovals,
        knownApprovals,
      ),
    [
      awaitingReplyTechnicianIds,
      data?.recipients,
      freshApprovals,
      knownApprovals,
    ],
  );
  const filteredRecipients = useMemo(() => {
    const teamRecipients =
      teamIds.length === 0
        ? recipients
        : recipients.filter((recipient) =>
            recipient.team_access_key_ids?.some((id) => teamIds.includes(id)),
          );
    return multiFieldSearch(teamRecipients, technicianSearch, ["name"]);
  }, [recipients, teamIds, technicianSearch]);
  const {
    selectedRecipientIds,
    eligibleRecipients,
    selectedEligibleRecipients,
    allEligibleSelected,
    someEligibleSelected,
    toggleRecipient,
    toggleAllEligible,
    selectOnly,
  } = useRecipientSelection({
    recipients: filteredRecipients,
    selectionKey: `${businessId ?? ""}:${periodStart ?? ""}`,
    enabled: !!data && !previewOnly,
  });
  const { noteDrafts, setNoteDraft, trackSubmittedNotes } =
    usePayrollStatementNoteDrafts(recipients);
  useSentPackDriftRefresh({
    recipients,
    states: freshApprovals?.sent_pack_states,
    refetch: refetchNotifications,
    pollUpdatedAt: approvalsUpdatedAt,
    enabled: !!data && !previewOnly,
  });
  const changedRecipients = useMemo(
    () => getRecipientsWithChangedPacks(filteredRecipients),
    [filteredRecipients],
  );
  const blockedChangedRecipients = useMemo(
    () => getBlockedChangedPackRecipients(filteredRecipients),
    [filteredRecipients],
  );
  const selectChangedPacks = useCallback(() => {
    selectOnly(changedRecipients);
  }, [changedRecipients, selectOnly]);
  const detailRecipient =
    filteredRecipients.find(
      (recipient) => recipient.technician_id === detailTechnicianId,
    ) ?? null;
  const periodLabel = formatNotificationPeriod({
    startDate: data?.period?.start_date,
    endDate: data?.period?.end_date,
    fallback: periodStart ?? "",
  });
  const canShowRecipients =
    !error && (!projectionBuild || projectionBuild.status === "ready");
  const sendDisabledReason = getSendDisabledReason({
    packsReady: canShowRecipients,
    canSend: data?.can_send ?? false,
    cannotSendReason: data?.cannot_send_reason,
    selectedEligibleCount: selectedEligibleRecipients.length,
  });
  const {
    send,
    sendFailures,
    activeSendResponse,
    isSending,
    isSavingNote,
    isSubmitting: isSendSubmitting,
  } = useSendPayrollStatements({
    businessId,
    periodStart,
    previewOnly,
    sendDisabledReason,
    selectedRecipients: selectedEligibleRecipients,
    noteDrafts,
    trackSubmittedNotes,
  });
  const deliveryPoll = usePayrollStatementDeliveryPolling(
    businessId,
    periodStart ?? null,
    activeSendResponse,
  );
  const missingReadyForEmailAccessKeys = getMissingTechApprovalAccessKeys(
    periodDetail?.approval_status,
  );
  const isProjectionBuilding =
    data?.projection_build?.status !== "ready" &&
    projectionBuild?.status === "pending" &&
    !projectionBuild.polling_timed_out &&
    !canRetryProjectionHydration;
  const isPayPacksLoading = isLoading || isProjectionBuilding;
  const showRecipientContent = !isPayPacksLoading && canShowRecipients;

  const closeDetail = useCallback(() => setDetailTechnicianId(null), []);

  const openDetail = (
    recipient: PayrollStatementNotificationRecipient,
    view: SidebarView,
  ) => {
    setDetailTechnicianId(recipient.technician_id);
    setSidebarView(view);
  };
  return {
    teams,
    teamIds,
    setTeamIds,
    awaitingReplyTechnicianIds,
    canUsePayrollEmails,
    previewOnly,
    data,
    isLoading,
    error,
    projectionBuild,
    canRetryProjectionHydration,
    isProjectionRefreshing,
    refetchProjectionBuild,
    retryProjectionBuild,
    isRetryingProjection,
    recipients,
    filteredRecipients,
    selectedRecipientIds,
    eligibleRecipients,
    selectedEligibleRecipients,
    allEligibleSelected,
    someEligibleSelected,
    toggleRecipient,
    toggleAllEligible,
    changedRecipients,
    blockedChangedRecipients,
    selectChangedPacks,
    noteDrafts,
    setNoteDraft,
    detailTechnicianId,
    detailRecipient,
    sidebarView,
    setSidebarView,
    openDetail,
    closeDetail,
    technicianSearch,
    setTechnicianSearch,
    periodLabel,
    sendDisabledReason,
    send,
    sendFailures,
    isSending,
    isSavingNote,
    isSendSubmitting,
    deliveryPoll,
    missingReadyForEmailAccessKeys,
    isProjectionBuilding,
    canShowRecipients,
    isPayPacksLoading,
    showRecipientContent,
    confirmSendOpen,
    setConfirmSendOpen,
    businessId,
    periodStart: periodStart ?? null,
  };
};
